import type FundOpportunity from '../../models/FundOpportunity';
import type { InvestorAllocationsResponse } from '../../models/InvestmentEntity';
import type Investor from '../../models/Investor';
import { currentInvestment } from '../../models/Investor';
import type IRRDataPoint from '../../models/IRRDataPoint';
import type { IRRData } from '../../models/IRRDataPoint';

export type FundUpdateChartType = 'Cumulative' | 'Discrete';

export function toMoneyMultiple(returnOnInvestment: FundOpportunity['returnOnInvestment']) {
  if (!returnOnInvestment) return returnOnInvestment;

  return (100 + Number(returnOnInvestment)) / 100;
}

export function getSelectedInvestmentAmount(
  investor: Investor | undefined,
  allocationsData: InvestorAllocationsResponse | undefined,
  selectedEntityIds: Set<string>,
  simulatedInvestment?: number,
): number {
  if (!investor) {
    return simulatedInvestment ?? 0;
  }

  if (!allocationsData) {
    return currentInvestment(investor) ?? 0;
  }

  let selectedAmount = 0;
  const entityGroups = new Map<string, number>();

  allocationsData.allocations.forEach((allocation) => {
    const entityId = allocation.investmentEntityId;
    const amount = Number(allocation.allocatedAmount);

    if (entityGroups.has(entityId)) {
      entityGroups.set(entityId, entityGroups.get(entityId)! + amount);
    } else {
      entityGroups.set(entityId, amount);
    }
  });

  entityGroups.forEach((amount, entityId) => {
    if (selectedEntityIds.has(entityId)) {
      selectedAmount += amount;
    }
  });

  return selectedAmount;
}

export function mergeInvestorAllocationResponses(
  allAllocationsData: InvestorAllocationsResponse[],
): InvestorAllocationsResponse {
  const mergedAllocations = allAllocationsData.flatMap((allocData) => allocData.allocations);
  let totalCommitted = 0;
  let totalAllocated = 0;

  allAllocationsData.forEach((allocData) => {
    totalCommitted += Number(allocData.committedInvestment ?? 0);
    totalAllocated += allocData.totalAllocated || 0;
  });

  return {
    allocations: mergedAllocations,
    totalAllocated,
    remainingAllocation: totalCommitted - totalAllocated,
    committedInvestment: totalCommitted,
  };
}

export function buildEntityGroupsForChart(
  allocations: InvestorAllocationsResponse['allocations'],
  selectedEntityIds: Set<string>,
): Map<string, { name: string; amount: number }> {
  const entityGroups = new Map<string, { name: string; amount: number }>();

  allocations.forEach((allocation) => {
    const entityId = allocation.investmentEntityId;
    const amount = parseFloat(allocation.allocatedAmount);
    const name = allocation.investmentEntityName || `Entity ${entityId}`;

    if (selectedEntityIds.has(entityId)) {
      if (entityGroups.has(entityId)) {
        entityGroups.get(entityId)!.amount += amount;
      } else {
        entityGroups.set(entityId, { name, amount });
      }
    }
  });

  return entityGroups;
}

export function buildMultiChartData(
  chartData: IRRDataPoint[],
  entityGroups: Map<string, { name: string; amount: number }>,
  fundSize: number,
  chartType: FundUpdateChartType,
): Record<string, unknown>[] {
  return chartData.map((point) => {
    const result: Record<string, unknown> = { month: point.month };

    entityGroups.forEach((entity) => {
      const ratio = entity.amount / fundSize;
      result[entity.name] = {
        type: point.type || 'actual',
        value:
          chartType === 'Cumulative'
            ? (point.cumulative_value ?? 0) * ratio
            : (point.discrete_value ?? 0) * ratio,
      };
    });

    return result;
  });
}

export function normalizeIrrDataForInvestmentRatio(rawData: IRRData, ratio: number): IRRData {
  const normalized = rawData.historical.map((point) => ({
    ...point,
    cumulative_value: (point.cumulative_value ?? 0) * ratio,
    discrete_value: (point.discrete_value ?? 0) * ratio,
  }));

  return {
    ...rawData,
    historical: normalized,
  };
}

export function preselectAllocationState(allocationsData: InvestorAllocationsResponse) {
  const selectedEntityIds = new Set<string>(
    allocationsData.allocations.map((allocation) => allocation.investmentEntityId),
  );
  const includeUnallocated = allocationsData.remainingAllocation > 0;

  return { selectedEntityIds, includeUnallocated };
}
